/**
 * Layout for the skill map.
 *
 * Kept out of the component so it can be tested without a DOM, and so the one
 * genuinely fiddly part — turning a concept graph into positions — is somewhere a
 * volunteer can read it.
 *
 * The layout is **deterministic**, not a force simulation: x is the concept's tier,
 * y is its slot within that tier. A physics layout looks impressive and then moves
 * every time you open it, which is the opposite of what a map is for — a learner
 * should recognise their own map from last week. It is also the kind of clever that
 * only one person ends up understanding.
 */

import type { MapConcept, SkillMap } from "./api";

export interface PlacedConcept extends MapConcept {
  x: number;
  y: number;
  /** Index into SkillMap.bands. 0 is the lowest band the domain defines. */
  bandIndex: number;
  /** 0..1, derived from bandIndex. Drives radius and brightness. */
  intensity: number;
  stageIndex: number;
  /** Hue in degrees for this concept's stage. See hueForStage. */
  hue: number;
  /** Every prerequisite met, but not started yet: this is what to do next. */
  readyToStart: boolean;
}

export interface MapEdge {
  from: string;
  to: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** True when the prerequisite is not yet met — the edge the learner is blocked on. */
  gap: boolean;
}

export interface MapLayout {
  concepts: PlacedConcept[];
  edges: MapEdge[];
  width: number;
  height: number;
  stages: string[];
  tiers: number[];
}

export const COLUMN = 132;
export const ROW = 74;
export const MARGIN_X = 74;
export const MARGIN_Y = 52;

/**
 * Which band a concept falls in, as an index.
 *
 * Matching on the band *text* would hardcode one domain's vocabulary; the index
 * works for any graph that defines its own `mastery_scale`. Falls back to a mastery
 * cut only if the band string is not in the list at all.
 */
export function bandIndex(bands: string[], concept: { band: string; mastery: number }): number {
  const found = bands.indexOf(concept.band);
  if (found >= 0) return found;
  if (bands.length === 0) return 0;
  return Math.min(bands.length - 1, Math.floor(concept.mastery * bands.length));
}

/**
 * A hue per stage, kept clear of the hues that already carry meaning.
 *
 * Colour here is the *secondary* encoding: brightness and size carry mastery, hue
 * only says which region of the graph a concept belongs to. So the palette must not
 * borrow a colour that already means something. Two are spoken for, and these are
 * measured from the stylesheet rather than guessed:
 *
 *   --warn, hue  42 — the ring on "your project needs this", and the halo on a
 *                     concept that moved this session
 *   --ok,   hue 133 — the outline on "ready to start"
 *
 * The arc 165° → 5° (through blue, violet, magenta, red) clears both with room to
 * spare. The first version of this ran 165° → 40° and put the last stage on almost
 * exactly the project gold, which is the collision this function exists to prevent.
 *
 * With a dozen stages the hues get close together. That is acceptable because stages
 * are also clustered spatially: the colour is there to make the regions visible, not
 * to be read off precisely.
 */
export const HUE_START = 165;
export const HUE_SPAN = 200;
/** Hues that mean something on their own, and the clearance stages must keep from them. */
export const RESERVED_HUES = [42, 133];
export const RESERVED_CLEARANCE = 20;

export function hueForStage(index: number, stageCount: number): number {
  if (stageCount <= 1) return HUE_START;
  const clamped = Math.min(Math.max(index, 0), stageCount - 1);
  return Math.round((HUE_START + (HUE_SPAN * clamped) / (stageCount - 1)) % 360);
}

export function intensity(index: number, bandCount: number): number {
  if (bandCount <= 1) return index > 0 ? 1 : 0;
  return index / (bandCount - 1);
}

/** Node radius in px. Bigger means more solid, so the map reads at a glance. */
export function radiusFor(concept: PlacedConcept): number {
  return 7 + concept.intensity * 9;
}

export function layoutMap(map: SkillMap): MapLayout {
  const stages = map.stages.map((s) => s.name);
  const all: MapConcept[] = map.stages.flatMap((s) => s.concepts);
  const bandCount = map.bands.length;

  // Group by tier: the graph's own notion of "how far in", so the map reads left to
  // right as a journey rather than as an arbitrary cloud.
  const tiers = [...new Set(all.map((c) => c.tier))].sort((a, b) => a - b);
  const byTier = new Map<number, MapConcept[]>();
  for (const tier of tiers) byTier.set(tier, []);
  for (const concept of all) byTier.get(concept.tier)!.push(concept);

  // Within a tier, keep stage-mates together and order stably. Stable ordering is
  // the whole point: the same graph must lay out identically every time.
  for (const group of byTier.values()) {
    group.sort((a, b) => {
      const byStage = stages.indexOf(a.stage) - stages.indexOf(b.stage);
      return byStage !== 0 ? byStage : a.label.localeCompare(b.label);
    });
  }

  const placed = new Map<string, PlacedConcept>();
  const concepts: PlacedConcept[] = [];
  tiers.forEach((tier, column) => {
    const group = byTier.get(tier)!;
    // Centre each column vertically so short tiers do not hang off the top.
    const tallest = Math.max(...tiers.map((t) => byTier.get(t)!.length));
    const offset = ((tallest - group.length) * ROW) / 2;
    group.forEach((concept, slot) => {
      const index = bandIndex(map.bands, concept);
      const node: PlacedConcept = {
        ...concept,
        x: MARGIN_X + column * COLUMN,
        y: MARGIN_Y + offset + slot * ROW,
        bandIndex: index,
        intensity: intensity(index, bandCount),
        stageIndex: Math.max(0, stages.indexOf(concept.stage)),
        hue: hueForStage(Math.max(0, stages.indexOf(concept.stage)), stages.length),
        readyToStart: concept.unlocked && concept.mastery === 0,
      };
      placed.set(concept.id, node);
      concepts.push(node);
    });
  });

  const edges: MapEdge[] = [];
  for (const concept of concepts) {
    for (const prereq of concept.prerequisites) {
      const from = placed.get(prereq);
      if (!from) continue; // a prerequisite the graph no longer contains
      edges.push({
        from: prereq,
        to: concept.id,
        x1: from.x,
        y1: from.y,
        x2: concept.x,
        y2: concept.y,
        gap: concept.prereq_gaps.includes(prereq),
      });
    }
  }

  const tallest = Math.max(1, ...tiers.map((t) => byTier.get(t)!.length));
  return {
    concepts,
    edges,
    width: MARGIN_X * 2 + Math.max(0, tiers.length - 1) * COLUMN,
    height: MARGIN_Y * 2 + Math.max(0, tallest - 1) * ROW,
    stages,
    tiers,
  };
}

/**
 * What to say about a concept in one line, in the domain's own words.
 *
 * Never a percentage. docs/backlog.md is explicit that mastery is not a score and
 * a map that renders 0.6 as "60%" invites two kids to compare numbers, which is the
 * opposite of what the graph means — its author's word for 0.6 is "can do it with
 * hints".
 */
export function describe(concept: PlacedConcept): string {
  if (concept.mastery === 0 && concept.prereq_gaps.length > 0) {
    return `needs ${concept.prereq_gaps.length} thing${
      concept.prereq_gaps.length === 1 ? "" : "s"
    } first`;
  }
  if (concept.readyToStart) return "ready when you are";
  return concept.band;
}

/** Concepts due for review today or earlier. */
export function reviewDue(concepts: PlacedConcept[], today: Date = new Date()): PlacedConcept[] {
  const cutoff = today.toISOString().slice(0, 10);
  return concepts.filter((c) => c.next_review !== null && c.next_review <= cutoff);
}
